import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { Sort, SortDirection } from "@angular/material/sort";
import { FieldType } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import * as UserStateActions from "@vp/data-access/users";
import { UserFilter, UserState, UserStateService } from "@vp/data-access/users";
import { Tag, User, UserTypeFriendlyId } from "@vp/models";
import { TagSelectorContext } from "@vp/shared/components/tag-selector";
import { filterNullMap } from "@vp/shared/operators";
import { PermissionsConstService } from "@vp/shared/permissions-const";
import { compare } from "@vp/shared/utilities";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { combineLatest, Observable, Subject } from "rxjs";
import { map, switchMap, take, takeUntil } from "rxjs/operators";

export interface FlattenedUser {
  userId: string;
  userName: string;
  userEmail: string;
  userRoles: string;
  userTags: string;
  active: boolean;
  userLastLoginDate: string;
}

@Component({
  selector: "vp-edit-users-list",
  standalone: false,
  templateUrl: "./edit-users-list.component.html",
  styleUrls: ["./edit-users-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TagSelectorContext, UserStateService]
})
export class EditUsersListComponent extends FieldType implements OnInit, OnDestroy {
  users$: Observable<User[]> = inject(Store).select(UserState.users);
  currentFilter$: Observable<UserFilter> = inject(Store).select(UserState.currentFilter);
  selectedTag$: Observable<Tag | null> = inject(Store).select(TagManagerState.selectedTag);

  displayedColumns: string[] = ["profileFirstName", "email"];

  private readonly _destroyed$ = new Subject<void>();

  constructor(
    public permConst: PermissionsConstService,
    private store: Store
  ) {
    super();
  }

  flattenedUsers$ = combineLatest([
    this.users$,
    this.selectedTag$.pipe(filterNullMap()),
    this.currentFilter$
  ]).pipe(
    map(([users, selectedTag, userFilter]: [User[], Tag, UserFilter]) => {
      const assignedUsers = users.filter(user => user.assignedTags.includes(selectedTag.tagId));
      const mappedUsers = mapUsers(assignedUsers);
      const sort: Sort = {
        active: userFilter.sort ?? "",
        direction: (userFilter.sortDirection as SortDirection) ?? ""
      };
      const sortedUsers = sortUsers(mappedUsers, sort);
      return sortedUsers;
    }),
    takeUntil(this._destroyed$)
  );

  ngOnInit(): void {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((selectedTag: Tag) => {
          return this.store.dispatch(
            new UserStateActions.SetFilter({
              take: 0,
              skip: 0,
              sort: "profileFirstName",
              sortDirection: "asc",
              tags: [selectedTag.tagId],
              userType: [UserTypeFriendlyId.loginUser]
            })
          );
        }),
        takeUntil(this._destroyed$)
      )
      .subscribe(() => {
        this.store.dispatch(new UserStateActions.GetFiltered());
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.store.dispatch(new UserStateActions.ResetState());
  }
}

const mapUsers = (users: User[]): FlattenedUser[] => {
  const flattenedUserArray: FlattenedUser[] = [];
  users?.forEach(user => {
    const userArray = {} as FlattenedUser;
    userArray.userEmail = user.email;
    let suffix = "";
    let salutation = "";
    if (user.profile.salutation) {
      salutation = user.profile.salutation + " ";
    }
    if (user.profile.suffix) {
      suffix = ", " + user.profile.suffix;
    }
    userArray.userName = salutation + user.profile.firstName + " " + user.profile.lastName + suffix;
    flattenedUserArray.push(userArray);
  });
  return flattenedUserArray;
};

const sortUsers = (users: FlattenedUser[], sort: Sort) => {
  if (!sort.active || sort.direction === "") {
    return users;
  }
  const isAsc = sort.direction === "asc";
  switch (sort.active) {
    case "profileFirstName":
      return users.sort((a, b) => compare(a.userName, b.userName, isAsc));
    case "email":
      return users.sort((a, b) => compare(a.userEmail, b.userEmail, isAsc));
    default:
      return users;
  }
};
